import { gql } from '@apollo/client'

/**
 * SPR-571 (4.1 + 4.2): Saved Filter Views — Maestro recruiter GraphQL ops.
 *
 * A saved view's `filters` is OPAQUE JSON (round-tripped verbatim by the
 * store). Queries + mutations live together because the saved-filter-views
 * store consumes them as one cohesive surface.
 */

/**
 * ⚠️ Do NOT turn these selections back into a `fragment ... on
 * MaestroSavedFilterView` spread.
 *
 * Both Apollo clients in this app are built with `new InMemoryCache({
 * addTypename: false })` (apollo.client.js). Without `__typename` in the
 * response, Apollo cannot decide whether a type-conditioned fragment applies,
 * so a selection set consisting ONLY of a fragment spread resolves to `{}` for
 * every row — the network response is complete, but `data.savedFilterViews`
 * comes back as `[{}, {}, {}]`.
 *
 * That is exactly what shipped: saved views loaded nothing from BE on any tab
 * (verified on staging — BE returned three full rows, the store received three
 * empty objects). Fields are inlined below so the result survives
 * `addTypename: false`.
 */
const SAVED_FILTER_VIEW_FIELDS_SDL = `
	id
	name
	tabName
	scope
	ownerId
	filters
	sortField
	sortOrder
	createdAt
	updatedAt
`

export const SAVED_FILTER_VIEWS_QUERY = gql`
	query savedFilterViews($tabName: String!) {
		savedFilterViews(tabName: $tabName) {
			${SAVED_FILTER_VIEW_FIELDS_SDL}
		}
	}
`

export const SAVED_FILTER_VIEW_DEFAULTS_QUERY = gql`
	query savedFilterViewDefaults {
		savedFilterViewDefaults {
			byUser {
				tabName
				viewId
			}
			byRole {
				roleName
				tabName
				viewId
			}
		}
	}
`

export const CREATE_SAVED_FILTER_VIEW_MUTATION = gql`
	mutation createSavedFilterView($input: CreateSavedFilterViewInput!) {
		createSavedFilterView(input: $input) {
			${SAVED_FILTER_VIEW_FIELDS_SDL}
		}
	}
`

export const UPDATE_SAVED_FILTER_VIEW_MUTATION = gql`
	mutation updateSavedFilterView($id: ID!, $input: UpdateSavedFilterViewInput!) {
		updateSavedFilterView(id: $id, input: $input) {
			${SAVED_FILTER_VIEW_FIELDS_SDL}
		}
	}
`

export const DELETE_SAVED_FILTER_VIEW_MUTATION = gql`
	mutation deleteSavedFilterView($id: ID!) {
		deleteSavedFilterView(id: $id) {
			success
		}
	}
`

export const SET_SAVED_FILTER_VIEW_MY_DEFAULT_MUTATION = gql`
	mutation setSavedFilterViewMyDefault($tabName: String!, $viewId: ID) {
		setSavedFilterViewMyDefault(tabName: $tabName, viewId: $viewId) {
			success
		}
	}
`

export const SET_SAVED_FILTER_VIEW_ROLE_DEFAULT_MUTATION = gql`
	mutation setSavedFilterViewRoleDefault($roleName: String!, $tabName: String!, $viewId: ID) {
		setSavedFilterViewRoleDefault(roleName: $roleName, tabName: $tabName, viewId: $viewId) {
			success
		}
	}
`
